
import React, { useState, useEffect } from "react";
import { CheckCircle, RefreshCw, ArrowRight } from "lucide-react";
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from "react-router-dom";

import { setField } from "../../../slices/auth";
import { getDefaultLandingPath } from "../../../utils/getDefaultLandingPath";

const DataSyncingStep = ({ data }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState("");
  const [currentStepKey, setCurrentStepKey] = useState('products');
  const [syncStatus, setSyncStatus] = useState({
    products: false,
    orders: false,
    inventory: false,
    analytics: false,
  });
  const [isCompleted, setIsCompleted] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const { currentUser } = useSelector(state => state?.auth || {});
  const startedAt = currentUser?.store_sync_started_at; 

  const TOTAL_MS = 30 * 60 * 1000; 

  const syncSteps = [
    { key: 'products', label: 'Syncing product catalog', duration: 2000 },
    { key: 'orders', label: 'Importing order history', duration: 1500 },
    { key: 'inventory', label: 'Updating inventory levels', duration: 1000 },
    { key: 'analytics', label: 'Processing analytics data', duration: 1500 },
  ];

  useEffect(() => {
    // If we don't have a start time, fall back to a short animated demo (10s)
    let intervalId;
    let completionTimer;
    const now = Date.now();
    const startedMs = startedAt ? new Date(startedAt).getTime() : now;
    const elapsedInitial = Math.max(0, now - startedMs);

    // If 30 minutes already passed, complete immediately
    if (elapsedInitial >= TOTAL_MS) {
      setProgress(100);
      setShowCompletion(true);
      completionTimer = setTimeout(() => {
        setIsCompleted(true);
        setShowCompletion(false);
        const completionData = {
          timestamp: Date.now().toString(),
          connectedAccounts: connectedAccounts,
          syncCompleted: true,
          hasConnections: hasConnections
        };
        localStorage.setItem("dataSyncingCompleted", JSON.stringify(completionData));
      }, 1000);
      return () => {
        completionTimer && clearTimeout(completionTimer);
      };
    }

    const initialPercent = Math.min(99, Math.floor((elapsedInitial / TOTAL_MS) * 100));
    setProgress(initialPercent);
    const applyStepStatus = (pct) => {
      const thresholds = [25, 50, 75, 100];
      const keys = ['products', 'orders', 'inventory', 'analytics'];
      const updated = { products: false, orders: false, inventory: false, analytics: false };
      thresholds.forEach((t, idx) => {
        if (pct >= t) updated[keys[idx]] = true;
      });
      // Determine current step key based on progress
      let idx = 0;
      if (pct >= 75) idx = 3;
      else if (pct >= 50) idx = 2;
      else if (pct >= 25) idx = 1;
      else idx = 0;
      setCurrentStepKey(keys[idx]);
      setSyncStatus(prev => ({ ...prev, ...updated }));
    };
    applyStepStatus(initialPercent);

    intervalId = setInterval(() => {
      const nowTick = Date.now();
      const elapsed = Math.max(0, nowTick - startedMs);
      const pct = Math.min(100, (elapsed / TOTAL_MS) * 100);
      setProgress(pct);

      applyStepStatus(pct);

      if (pct >= 100) {
        setProgress(100);
        setShowCompletion(true);
        // Delay the final screen by 1s to show the completion label
        completionTimer = setTimeout(() => {
          setIsCompleted(true);
          setShowCompletion(false);
          const completionData = {
            timestamp: Date.now().toString(),
            connectedAccounts: connectedAccounts,
            syncCompleted: true,
            hasConnections: hasConnections
          };
          localStorage.setItem("dataSyncingCompleted", JSON.stringify(completionData));
          clearInterval(intervalId);
        }, 1000);
      }
    }, 1000);

    return () => {
      intervalId && clearInterval(intervalId);
      completionTimer && clearTimeout(completionTimer);
    };
  }, [startedAt, TOTAL_MS]);

  // Listen for completion and navigation events from other tabs
  useEffect(() => {
    const handleStorageEvent = (e) => {
      if (e.key === "dataSyncingCompleted" && e.newValue) {
        try {
          const completionData = JSON.parse(e.newValue);
          if (completionData.syncCompleted) {
            localStorage.removeItem("dataSyncingCompleted");
            setIsCompleted(true);
            setProgress(100);
            setCurrentStep("Sync completed successfully!");
          }
        } catch (error) {
          console.error("Error parsing completion data:", error);
        }
      } else if (e.key === "onboardingNavigation" && e.newValue) {
        try {
          const navData = JSON.parse(e.newValue);
          // navData.path is the cross-tab "sync completed" marker; the actual destination is the
          // role's default landing so both tabs agree (seller/seller+warehouse -> dashboard).
          if (navData.path === '/inventory/sku' && navData.syncCompleted) {
            dispatch(setField({
              module: 'currentUser',
              field: 'sw_store_sync_flag',
              value: true
            }));

            navigate(getDefaultLandingPath(currentUser), { replace: true });
          }
        } catch (error) {
          console.error("Error parsing navigation data:", error);
        }
      }
    };

    window.addEventListener("storage", handleStorageEvent);
    return () => {
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, [dispatch, navigate]);

  const handleGoToDashboard = () => {
    dispatch(setField({
      module: 'currentUser',
      field: 'sw_store_sync_flag',
      value: true
    }));

    // Notify other tabs about the navigation
    localStorage.setItem('onboardingNavigation', JSON.stringify({
      path: '/inventory/sku',
      timestamp: Date.now(),
      syncCompleted: true
    }));

    // Seller / seller+warehouse land on the dashboard; other roles on the SKU list.
    navigate(getDefaultLandingPath(currentUser), { replace: true });
  };

  const connectedAccounts = data.connectedAccounts || { amazon: true, walmart: false };
  const hasConnections = connectedAccounts.amazon || connectedAccounts.walmart;

  const currentStepLabel = (showCompletion || isCompleted)
    ? 'Sync completed successfully!'
    : (syncSteps.find(s => s.key === currentStepKey)?.label) || '';

  return (
    <div className="max-w-3xl mx-auto">
      <div className="onboarding-card text-center animate-fade-in">
        {!isCompleted ? (
          <>
            <div className="mb-8">
              <RefreshCw className="w-16 h-16 text-primary mx-auto animate-spin-slow" />
            </div>

                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Setting Up Your MarketOS Dashboard
                </h2>
            
              <p className="text-muted-foreground text-lg mb-8 font-medium">
                We're pulling in your products, orders, and inventory from your connected accounts.
                <br />
                This may take a few minutes. You'll receive an email when everything is ready.
              </p>

            <div className="mb-8">
              <div className="w-full bg-muted rounded-full h-3 mb-4">
                <div 
                  className="bg-primary h-3 rounded-full transition-all duration-300 ease-out" 
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-sm text-muted-foreground font-medium">
                {currentStepLabel}
              </p>
            </div>

              <div className="bg-accent rounded-lg p-6 mb-8">
                <h3 className="font-bold mb-4 sync-heading">Sync Progress</h3>
                <div className="grid grid-cols-2 gap-3">
                  {syncSteps.map((step) => (
                    <div key={step.key} className="flex items-center justify-start">
                      {syncStatus[step.key] ? (
                        <CheckCircle className="w-5 h-5 icon-success mr-3" />
                      ) : (
                        <div className="w-5 h-5 border-2 border-muted rounded-full mr-3" />
                      )}
                      <span style={{fontWeight: '500'}} className={`sync-points text-sm ${syncStatus[step.key] ? 'text-success' : 'text-muted-foreground'}`}>
                        {step.label.replace('Syncing ', '').replace('Importing ', '').replace('Updating ', '').replace('Processing ', '')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
          </>
        ) : (
          <div className="animate-fade-in">
            <div className="mb-8">
              <CheckCircle className="w-20 h-20 text-success mx-auto animate-pulse-success" />
            </div>

                <h2 className="text-3xl font-bold text-foreground mb-3">
                  Welcome to MarketOS!
                </h2>
            
            <p className="text-muted-foreground font-medium text-lg mb-8">
              Your account has been set up successfully. You're ready to start managing 
              your marketplace operations with powerful tools and insights.
            </p>

            <div className="bg-success/10 border-success/20 border rounded-lg p-6 mb-8">
              <div className="flex items-center justify-center mb-3">
                <CheckCircle className="w-6 h-6 text-success mr-2" />
                <span className="font-bold text-success">Setup Complete</span>
              </div>
              <div className="row text-sm justify-items-center g-3">
                <div className="flex items-center col-md-6 justify-center">
                  <CheckCircle className="w-4 h-4 text-success mr-2" />
                  <span>Account created</span>
                </div>
                <div className="flex items-center col-md-6 justify-center">
                  <CheckCircle className="w-4 h-4 text-success mr-2" />
                  <span>Email verified</span>
                </div>
                <div className="flex items-center col-md-6 justify-center">
                  <CheckCircle className="w-4 h-4 text-success mr-2" />
                  <span>Plan selected</span>
                </div>
                <div className="flex items-center col-md-6 justify-center">
                  <CheckCircle className="w-4 h-4 text-success mr-2" />
                  <span>Dashboard ready</span>
                </div>
              </div>
            </div>

            <button
              onClick={handleGoToDashboard}
              className="btn-primary font-semibold text-lg px-8 py-4"
              style={{height: '44px'}}
            >
              Enter Platform
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>

            <p className="text-sm text-muted-foreground mt-3 mb-0 font-medium">
              You'll receive an email confirmation with your account details.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export { DataSyncingStep };
